import { useState, useEffect } from "react";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { useSearchParams, useNavigate } from "react-router-dom";
import { auth } from "../../services/firebaseAuth";

import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Snackbar,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import isStrongPassword from "validator/lib/isStrongPassword";
import CustomTextField from "../Base/CustomTextField";

const ConfirmResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get("oobCode");

  const [newPassword, setNewPassword] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // Vérifie que le code est valide
  useEffect(() => {
    const checkCode = async () => {
      if (!oobCode) {
        setErrorMsg("Code de réinitialisation manquant ou invalide.");
        setLoading(false);
        return;
      }
      try {
        await verifyPasswordResetCode(auth, oobCode);
      } catch {
        setErrorMsg("Ce lien de réinitialisation n’est plus valide.");
      } finally {
        setLoading(false);
      }
    };
    checkCode();
  }, [oobCode]);

  const handleSubmit = async () => {
    if (!oobCode) return;
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccessMsg("Mot de passe réinitialisé avec succès !");
      setSnackOpen(true);
      setTimeout(() => navigate("/login"), 3000); // Redirection après succès
    } catch {
      setErrorMsg("Erreur lors de la réinitialisation. Réessayez.");
    }
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      style={{ minHeight: "80vh" }}
    >
      <Card sx={{ maxWidth: 450, width: "100%", p: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            🔐 Réinitialisation du mot de passe
          </Typography>

          {loading && <CircularProgress />}

          {!loading && errorMsg && (
            <Alert severity="error" icon={<ErrorIcon />}>
              {errorMsg}
            </Alert>
          )}

          {!loading && !errorMsg && !successMsg && (
            <>
              <Typography sx={{ mb: 2 }}>
                Définissez un nouveau mot de passe sécurisé :
              </Typography>
              <CustomTextField
                label="Nouveau mot de passe"
                type="password"
                fullWidth
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setIsPasswordValid(isStrongPassword(e.target.value));
                }}
                helperText="8 caractères minimum, 1 majuscule, 1 minuscule, 1 chiffre, 1 caractère spécial"
                error={!isPasswordValid && newPassword.length > 0}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                fullWidth
                disabled={!isPasswordValid}
                onClick={handleSubmit}
              >
                Réinitialiser le mot de passe
              </Button>
            </>
          )}

          {successMsg && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              {successMsg} Redirection en cours...
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Snackbar en bas de l’écran */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message={successMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Grid>
  );
};

export default ConfirmResetPassword;
